import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Network, Building2, Users, Boxes, ChevronRight, ChevronDown } from "lucide-react";

export default function Allocation() {
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    api.get("/stock/allocation-tree").then(r => setTree(r.data)).finally(() => setLoading(false));
  }, []);

  const toggle = (id) => setExpanded({ ...expanded, [id]: !expanded[id] });

  const totals = tree.reduce((acc, d) => ({
    holds: acc.holds + d.holds,
    at_dealers: acc.at_dealers + d.at_dealers,
    consumed: acc.consumed + d.consumed,
  }), { holds: 0, at_dealers: 0, consumed: 0 });

  return (
    <div className="space-y-6">
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#64748B]">Distribution Tracker</div>
        <h1 className="text-3xl tracking-tighter font-medium mt-1">Stock Allocation</h1>
        <p className="text-[#475569] text-sm mt-1">Manufacturer → Distributor → Dealer flow across your network.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <StatBox label="At Distributors" value={totals.holds} icon={Building2} />
        <StatBox label="Passed to Dealers" value={totals.at_dealers} icon={Users} />
        <StatBox label="Consumed" value={totals.consumed} icon={Boxes} />
      </div>

      {loading ? <div className="text-sm text-[#475569]">Loading…</div> :
        tree.length === 0 ? (
          <div className="text-center py-16 text-[#64748B] border border-dashed border-[#E5E7EB] rounded-xl">
            <Network size={28} className="mx-auto mb-3" /> No approved distributors yet.
          </div>
        ) : (
          <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] overflow-hidden" data-testid="allocation-tree">
            <div className="grid grid-cols-12 px-4 py-3 border-b border-[#E5E7EB] text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">
              <div className="col-span-5">Distributor</div>
              <div className="col-span-2 text-right">Holding</div>
              <div className="col-span-2 text-right">At Dealers</div>
              <div className="col-span-2 text-right">Consumed</div>
              <div className="col-span-1 text-right">Dealers</div>
            </div>
            {tree.map((d) => {
              const isOpen = expanded[d.distributor_id];
              return (
                <div key={d.distributor_id} className="border-b border-[#E5E7EB]">
                  <button
                    data-testid={`alloc-row-${d.distributor_id}`}
                    onClick={() => toggle(d.distributor_id)}
                    className="w-full grid grid-cols-12 px-4 py-3 items-center hover:bg-[#F8FAFC] text-left"
                  >
                    <div className="col-span-5 flex items-center gap-2">
                      {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      <div>
                        <div className="text-sm font-medium">{d.distributor_name}</div>
                        <div className="text-[11px] text-[#64748B]">{d.distributor_city}</div>
                      </div>
                    </div>
                    <div className="col-span-2 text-right font-mono text-sm">{d.holds}</div>
                    <div className="col-span-2 text-right font-mono text-sm">{d.at_dealers}</div>
                    <div className="col-span-2 text-right font-mono text-sm">{d.consumed}</div>
                    <div className="col-span-1 text-right text-xs text-[#475569]">{d.dealers?.length || 0}</div>
                  </button>
                  {isOpen && d.dealers?.length > 0 && (
                    <div className="bg-[#FFFFFF] border-t border-[#E5E7EB] pl-10 pr-4 py-2">
                      <div className="grid grid-cols-12 py-2 text-[10px] uppercase tracking-[0.15em] text-[#64748B] font-bold">
                        <div className="col-span-4">Dealer</div>
                        <div className="col-span-2 text-right">Available</div>
                        <div className="col-span-2 text-right">Consumed</div>
                        <div className="col-span-4 text-right">Installations</div>
                      </div>
                      {d.dealers.map((de) => (
                        <div key={de.dealer_id} className="grid grid-cols-12 py-2 items-center border-t border-[#E5E7EB]" data-testid={`dealer-row-${de.dealer_id}`}>
                          <div className="col-span-4">
                            <div className="text-sm">{de.dealer_name}</div>
                            <div className="text-[11px] text-[#64748B]">{de.dealer_city}</div>
                          </div>
                          <div className="col-span-2 text-right font-mono text-sm">{de.available}</div>
                          <div className="col-span-2 text-right font-mono text-sm">{de.consumed}</div>
                          <div className="col-span-4 text-right font-mono text-sm text-[#E63946]">{de.installations}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  {isOpen && (!d.dealers || d.dealers.length === 0) && (
                    <div className="bg-[#FFFFFF] border-t border-[#E5E7EB] px-10 py-4 text-xs text-[#64748B]">No dealers under this distributor yet.</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
    </div>
  );
}

function StatBox({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#FFFFFF] p-5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#64748B] font-bold">{label}</div>
        {Icon && <Icon size={16} className="text-[#475569]" />}
      </div>
      <div className="mt-3 text-3xl font-medium text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>{value}</div>
    </div>
  );
}
